<template>
  <div class="pa-0">
    <!-- Search and Actions -->
    <v-row class="mb-4 align-center px-4">
      <v-col cols="12" md="6">
        <v-text-field
          v-model="search"
          label="Buscar grupo..."
          prepend-inner-icon="fas fa-search"
          outlined
          dense
          hide-details
          clearable
          class="modern-search-field"
        ></v-text-field>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="auto">
        <v-btn color="primary" depressed @click="create" class="px-6">
          <v-icon left small>fas fa-plus</v-icon> Nuevo Grupo
        </v-btn>
      </v-col>
    </v-row>

    <!-- Table -->
    <v-card outlined class="modern-card elevation-0 overflow-hidden">
      <v-data-table
        :headers="headers"
        :items="groups"
        :loading="loading"
        :search="search"
        sort-by="name"
        class="modern-table"
        :items-per-page="15"
        :footer-props="{
          showFirstLastPage: true,
          itemsPerPageOptions: [10, 15, 30, 50]
        }"
      >
        <template v-slot:item.id="{ item }">
          <span class="caption grey--text font-weight-bold">#{{ item.id }}</span>
        </template>

        <template v-slot:item.name="{ item }">
          <span class="body-2 font-weight-bold primary--text">{{ item.name }}</span>
        </template>

        <template v-slot:item.is_supply="{ item }">
          <v-chip x-small label :color="item.is_supply ? 'primary lighten-4' : 'success lighten-4'" :text-color="item.is_supply ? 'primary darken-2' : 'success darken-2'" class="font-weight-bold">
            <v-icon x-small left>{{ item.is_supply ? 'fas fa-box' : 'fas fa-cogs' }}</v-icon>
            {{ item.is_supply ? 'MATERIALES' : 'PROCESOS' }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-btn icon small color="primary" @click.stop="edit(item)">
            <v-icon small>fas fa-edit</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Modal -->
    <v-dialog v-model="showDialog" max-width="500px" persistent>
      <v-card outlined class="modern-card overflow-hidden">
        <v-sheet color="grey lighten-4" class="px-6 py-4 border-bottom">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-3">
              {{ newGroup.id > 0 ? 'fas fa-edit' : 'fas fa-plus-circle' }}
            </v-icon>
            <div>
              <div class="text-h6 font-weight-bold lh-1">
                {{ newGroup.id > 0 ? 'Actualizar Grupo' : 'Nuevo Grupo' }}
              </div>
              <div class="caption grey--text mt-1">Configuración de agrupación de costos</div>
            </div>
          </div>
        </v-sheet>

        <v-card-text class="pa-6 pt-8">
          <ValidationObserver v-slot="{ handleSubmit }">
            <v-form @submit.prevent="handleSubmit(newGroup.id > 0 ? updateGroup : createGroup)">
              <ValidationProvider name="Nombre" rules="required" v-slot="{ errors }">
                <v-text-field
                  v-model="newGroup.name"
                  label="Nombre del Grupo"
                  placeholder="Ej: Impresión Offset, Insumos Gráficos..."
                  outlined
                  dense
                  autofocus
                  hide-details="auto"
                  :error-messages="errors"
                  class="mb-4"
                ></v-text-field>
              </ValidationProvider>

              <div class="bg-grey-light pa-4 rounded-lg">
                <v-checkbox
                  v-model="newGroup.is_supply"
                  label="Este es un grupo de Materiales"
                  hide-details
                  dense
                  color="primary"
                  class="mt-0 font-weight-bold"
                ></v-checkbox>
                <div class="caption grey--text ml-8 mt-1">
                  Si se marca, aparecerá en la sección de Materiales. De lo contrario, aparecerá en Procesos.
                </div>
              </div>

              <v-divider class="my-6"></v-divider>

              <div class="d-flex justify-end">
                <v-btn text small @click="showDialog = false" class="mr-2">Cancelar</v-btn>
                <v-btn type="submit" color="primary" depressed small class="px-6">
                  <v-icon x-small class="mr-2">fas fa-save</v-icon>
                  Guardar
                </v-btn>
              </div>
            </v-form>
          </ValidationObserver>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { fetchGroups, createGroup, updateGroup } from '@/api/products'
import notifications from '@/utils/notifications'

export default {
  name: 'Group',
  data: () => ({
    groups: [],
    search: '',
    showDialog: false,
    loading: false,
    newGroup: { id: -1, name: '', is_supply: false },
    headers: [
      { text: 'ID', value: 'id', align: 'start', width: '80', class: 'grey lighten-4' },
      { text: 'Nombre del Grupo', value: 'name', align: 'start', class: 'grey lighten-4' },
      { text: 'Tipo de Grupo', value: 'is_supply', align: 'center', width: '150', class: 'grey lighten-4' },
      { text: 'Acciones', value: 'actions', align: 'center', width: '100', sortable: false, class: 'grey lighten-4' }
    ],
  }),
  methods: {
    edit(item) {
      this.newGroup = { ...item }
      this.showDialog = true
    },
    create() {
      this.newGroup = { id: -1, name: '', is_supply: false }
      this.showDialog = true
    },
    async createGroup() {
      try {
        await createGroup(this.newGroup)
        notifications.showSuccess('Grupo creado correctamente')
        this.showDialog = false
        this.refresh()
      } catch (e) {
        notifications.showError('Error al crear grupo')
      }
    },
    async updateGroup() {
      try {
        await updateGroup(this.newGroup)
        notifications.showSuccess('Grupo actualizado')
        this.showDialog = false
        this.refresh()
      } catch (e) {
        notifications.showError('Error al actualizar grupo')
      }
    },
    async refresh() {
      this.loading = true
      const res = await fetchGroups()
      this.groups = res
      this.$store.dispatch('getGroups')
      this.loading = false
    }
  },
  async created() {
    this.refresh()
  }
}
</script>

<style scoped>
.modern-card {
  border: 1px solid rgba(0,0,0,0.08) !important;
  border-radius: 12px !important;
}
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.08); }
.bg-grey-light { background-color: #f8f9fa; }
.lh-1 { line-height: 1.2; }
</style>
